import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import AppDropdown from '../../../components/AppDropdown';
import { useSchemesEntryScreen } from './hooks/useSchemesEntryScreen';
import { appToast } from '../../../components/AppToast';

function TextField({ label, value, onChange, placeholder, required = false, type = 'text' }) {
  return (
    <label className="grid gap-2">
      <span className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
        {label}
        {required ? <span className="text-error"> *</span> : null}
      </span>
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 py-3.5 text-sm text-on-surface outline-none placeholder:text-on-surface-variant/60 transition-all focus:border-primary focus:ring-1 focus:ring-primary/20 shadow-2xs"
      />
    </label>
  );
}

export default function SchemesEntryScreen() {
  const { state, actions } = useSchemesEntryScreen();

  useEffect(() => {
    if (state.error) appToast.error(state.error);
  }, [state.error]);

  useEffect(() => {
    if (state.success) appToast.success(state.success);
  }, [state.success]);

  const courseCountOptions = [
    { value: '1', label: '1 Course' },
    { value: '2', label: '2 Courses' },
    { value: '3', label: '3 Courses' },
    { value: '4', label: '4 Courses' },
    { value: '5', label: '5 Courses' },
    { value: '6', label: '6 Courses' },
    { value: '7', label: '7 Courses' },
    { value: '8', label: '8 Courses' },
    { value: '9', label: '9 Courses' },
  ];

  const installmentCountOptions = Array.from({ length: 12 }, (_, i) => ({
    value: String(i + 1),
    label: `${i + 1} Installment${i > 0 ? 's' : ''}`,
  }));

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* Top Header */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-on-surface-variant mb-1">
            <span>Education</span>
            <span className="material-symbols-outlined text-[12px]">chevron_right</span>
            <Link to="/masters/schemes/list" className="hover:text-primary transition-colors">
              Schemes Registry
            </Link>
            <span className="material-symbols-outlined text-[12px]">chevron_right</span>
            <span className="text-primary font-bold">{state.isEditMode ? 'Edit' : 'Create'}</span>
          </div>
          <h1 className="font-display text-4xl font-extrabold tracking-[-0.02em] text-on-surface">
            {state.isEditMode ? 'Edit Fee Scheme' : 'Create Fee Scheme'}
          </h1>
          <p className="mt-1 text-xs text-on-surface-variant font-medium">
            Configure course fee combinations, discount policies, and installment schedules.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/masters/schemes/list"
            className="inline-flex items-center justify-center rounded-xl bg-surface-container-high px-5 py-2.5 text-xs font-bold text-on-surface transition hover:bg-surface-container-highest cursor-pointer"
          >
            Discard
          </Link>
          <button
            type="button"
            onClick={actions.handleSave}
            disabled={state.isSaving}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-6 py-2.5 text-xs font-bold text-on-primary shadow-sm hover:brightness-105 active:scale-[0.98] transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {state.isSaving ? (
              <>
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-on-primary border-t-transparent" />
                <span>Saving...</span>
              </>
            ) : (
              <span>{state.isEditMode ? 'Update Scheme' : 'Save Scheme'}</span>
            )}
          </button>
        </div>
      </section>

      {state.isLoading ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-outline-variant/30 bg-surface-container-lowest px-6 py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
          <span className="mt-3 text-xs font-medium text-on-surface-variant">Loading scheme details...</span>
        </div>
      ) : (
        <section className="grid grid-cols-12 gap-6">
          {/* Main Left Column */}
          <div className="col-span-12 space-y-6 lg:col-span-8">
            {/* Card 1: General Identification */}
            <article className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-sm">
              <div className="mb-6 flex items-center gap-3">
                <div className="h-6 w-1 rounded-full bg-primary" />
                <h2 className="text-lg font-bold text-on-surface">General Identification</h2>
              </div>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                <div className="md:col-span-1">
                  <TextField
                    label="Scheme Name"
                    required
                    value={state.form.name}
                    onChange={(val) => actions.patchForm('name', val)}
                    placeholder="e.g. General Merit Concession"
                  />
                </div>

                <div className="md:col-span-1">
                  <label className="grid gap-2">
                    <span className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
                      No. of Courses
                    </span>
                    <AppDropdown
                      value={state.form.course_selection}
                      onChange={(val) => actions.handleCourseSelectionChange(val)}
                      options={courseCountOptions}
                      placeholder="Select course count"
                      triggerClassName="w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 py-3.5 text-sm text-on-surface outline-none transition-all focus:border-primary shadow-2xs"
                    />
                  </label>
                </div>

                <div className="md:col-span-1">
                  <TextField
                    label="Max. Discount %"
                    type="number"
                    value={state.form.max_discount}
                    onChange={(val) => actions.patchForm('max_discount', val)}
                    placeholder="0 to disable"
                  />
                </div>
              </div>
            </article>

            {/* Card 2: Course Fee Configurations */}
            <article className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-6 w-1 rounded-full bg-primary" />
                  <div>
                    <h2 className="text-lg font-bold text-on-surface">Course Fee Configurations</h2>
                    <p className="text-xs text-on-surface-variant">
                      Configure individual fees for each course in this combination scheme.
                    </p>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto rounded-xl border border-outline-variant/20">
                <table className="w-full text-left text-xs">
                  <thead className="border-b border-outline-variant/20 bg-surface-container-low text-[11px] font-bold uppercase tracking-wider text-on-surface-variant">
                    <tr>
                      <th className="py-3 pl-4 pr-2">A.Year</th>
                      <th className="px-2 py-3 min-w-[160px]">Course</th>
                      <th className="px-2 py-3 text-right">Admission Fee</th>
                      <th className="px-2 py-3 text-right">Tuition Fee</th>
                      <th className="px-2 py-3 text-right">Courseware Fee</th>
                      <th className="py-3 pl-2 pr-4 text-right">Fee %</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-outline-variant/10 text-on-surface">
                    {state.form.combo_grid.map((row, idx) => (
                      <tr key={idx} className="hover:bg-surface-container-low/30 transition-colors">
                        {/* AYear */}
                        <td className="py-3 pl-4 pr-2">
                          <AppDropdown
                            value={row.ayear}
                            onChange={(val) => actions.updateGridRow(idx, 'ayear', val)}
                            options={state.ayearOptions}
                            triggerClassName="w-28 rounded-lg border border-outline-variant/30 bg-surface-container-lowest px-2.5 py-1.5 text-xs text-on-surface"
                          />
                        </td>

                        {/* Course */}
                        <td className="px-2 py-3">
                          <AppDropdown
                            value={row.course_id}
                            onChange={(val) => actions.updateGridRow(idx, 'course_id', val)}
                            options={state.courseOptions}
                            placeholder="Select Course..."
                            triggerClassName="w-full min-w-[150px] rounded-lg border border-outline-variant/30 bg-surface-container-lowest px-2.5 py-1.5 text-xs text-on-surface"
                          />
                        </td>

                        {/* Admission Fee */}
                        <td className="px-2 py-3 text-right">
                          <input
                            type="number"
                            value={row.admission_fees}
                            onChange={(e) => actions.updateGridRow(idx, 'admission_fees', e.target.value)}
                            placeholder="0"
                            className="w-24 rounded-lg border border-outline-variant/30 bg-surface-container-lowest px-2.5 py-1.5 text-right text-xs text-on-surface outline-none focus:border-primary"
                          />
                        </td>

                        {/* Tuition Fee */}
                        <td className="px-2 py-3 text-right">
                          <input
                            type="number"
                            value={row.tution_fees}
                            onChange={(e) => actions.updateGridRow(idx, 'tution_fees', e.target.value)}
                            placeholder="0"
                            className="w-24 rounded-lg border border-outline-variant/30 bg-surface-container-lowest px-2.5 py-1.5 text-right text-xs text-on-surface outline-none focus:border-primary"
                          />
                        </td>

                        {/* Courseware Fee */}
                        <td className="px-2 py-3 text-right">
                          <input
                            type="number"
                            value={row.courseware_fees}
                            onChange={(e) => actions.updateGridRow(idx, 'courseware_fees', e.target.value)}
                            placeholder="0"
                            className="w-24 rounded-lg border border-outline-variant/30 bg-surface-container-lowest px-2.5 py-1.5 text-right text-xs text-on-surface outline-none focus:border-primary"
                          />
                        </td>

                        {/* Percentage */}
                        <td className="py-3 pl-2 pr-4 text-right">
                          <input
                            type="number"
                            value={row.percentage}
                            onChange={(e) => actions.updateGridRow(idx, 'percentage', e.target.value)}
                            placeholder="100"
                            className="w-20 rounded-lg border border-outline-variant/30 bg-surface-container-lowest px-2.5 py-1.5 text-right text-xs text-on-surface outline-none focus:border-primary"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals Summary Row */}
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-surface-container-low px-4 py-3 text-xs">
                <span className="font-bold text-on-surface">Calculated Totals:</span>
                <div className="flex flex-wrap items-center gap-4 text-on-surface-variant">
                  <span>
                    Admission: <strong className="text-on-surface">₹{state.totals.totalAdmission}</strong>
                  </span>
                  <span>•</span>
                  <span>
                    Tuition: <strong className="text-on-surface">₹{state.totals.totalTuition}</strong>
                  </span>
                  <span>•</span>
                  <span>
                    Courseware: <strong className="text-on-surface">₹{state.totals.totalCourseware}</strong>
                  </span>
                  <span>•</span>
                  <span>
                    Total %: <strong className="text-primary font-bold">{state.totals.totalPercentage}%</strong>
                  </span>
                </div>
              </div>
            </article>

            {/* Card 3: Associated Subjects */}
            <article className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-6 w-1 rounded-full bg-primary" />
                  <div>
                    <h2 className="text-lg font-bold text-on-surface">Associated Subjects</h2>
                    <p className="text-xs text-on-surface-variant">
                      Assign compulsory curriculum subjects required for this scheme.
                    </p>
                  </div>
                </div>

                <label className="relative inline-flex items-center cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={state.form.has_subjects}
                    onChange={(e) => actions.patchForm('has_subjects', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-surface-container-high peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>

              {state.form.has_subjects && (
                <div className="mt-5 pt-4 border-t border-outline-variant/15">
                  {state.subjectOptions.length === 0 ? (
                    <p className="text-xs text-on-surface-variant italic">No subjects available to associate.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {state.subjectOptions.map((sub) => {
                        const isSelected = state.form.subjects.includes(sub.value);
                        return (
                          <button
                            key={sub.value}
                            type="button"
                            onClick={() => actions.toggleSubject(sub.value)}
                            className={`inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-xs font-bold transition cursor-pointer select-none ${
                              isSelected
                                ? 'bg-primary text-on-primary shadow-xs'
                                : 'bg-surface-container-low text-on-surface hover:bg-surface-container-high'
                            }`}
                          >
                            <span className="material-symbols-outlined text-[14px]">
                              {isSelected ? 'check' : 'add'}
                            </span>
                            <span>{sub.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </article>

            {/* Card 4: Installment Schedule */}
            <article className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-6 w-1 rounded-full bg-primary" />
                  <div>
                    <h2 className="text-lg font-bold text-on-surface">Structured Installments</h2>
                    <p className="text-xs text-on-surface-variant">
                      Breakdown course fee payments across scheduled installment due dates.
                    </p>
                  </div>
                </div>

                <label className="relative inline-flex items-center cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={state.form.has_installment}
                    onChange={(e) => actions.patchForm('has_installment', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-surface-container-high peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>

              {state.form.has_installment && (
                <div className="mt-5 space-y-4 pt-4 border-t border-outline-variant/15">
                  <div className="w-full max-w-xs">
                    <label className="grid gap-2">
                      <span className="text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">
                        No. of Installments
                      </span>
                      <AppDropdown
                        value={state.form.no_of_installments}
                        onChange={(val) => actions.handleInstallmentCountChange(val)}
                        options={installmentCountOptions}
                        triggerClassName="w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 py-2.5 text-xs text-on-surface"
                      />
                    </label>
                  </div>

                  <div className="overflow-x-auto rounded-xl border border-outline-variant/20">
                    <table className="w-full text-left text-xs">
                      <thead className="border-b border-outline-variant/20 bg-surface-container-low text-[11px] font-bold uppercase tracking-wider text-on-surface-variant">
                        <tr>
                          <th className="py-2.5 pl-4 pr-2">#</th>
                          <th className="px-2 py-2.5">Due Date</th>
                          <th className="py-2.5 pl-2 pr-4 text-right">Percentage (%)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-outline-variant/10 text-on-surface">
                        {state.form.installment_grid.map((inst, idx) => (
                          <tr key={idx} className="hover:bg-surface-container-low/30 transition-colors">
                            <td className="py-2.5 pl-4 pr-2 font-bold text-on-surface-variant">
                              Inst {idx + 1}
                            </td>
                            <td className="px-2 py-2.5">
                              <input
                                type="date"
                                value={inst.installment_date}
                                onChange={(e) => actions.updateInstallmentRow(idx, 'installment_date', e.target.value)}
                                className="w-40 rounded-lg border border-outline-variant/30 bg-surface-container-lowest px-2.5 py-1.5 text-xs text-on-surface outline-none focus:border-primary"
                              />
                            </td>
                            <td className="py-2.5 pl-2 pr-4 text-right">
                              <input
                                type="number"
                                value={inst.percentage_amt}
                                onChange={(e) => actions.updateInstallmentRow(idx, 'percentage_amt', e.target.value)}
                                placeholder="0"
                                className="w-24 rounded-lg border border-outline-variant/30 bg-surface-container-lowest px-2.5 py-1.5 text-right text-xs text-on-surface outline-none focus:border-primary"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex items-center justify-between text-xs px-2 text-on-surface-variant">
                    <span>
                      Total Distribution: <strong className="text-on-surface">{state.installmentTotal}%</strong>
                    </span>
                    {Number(state.installmentTotal) !== 100 && (
                      <span className="text-error font-medium">Warning: Total should equal 100%</span>
                    )}
                  </div>
                </div>
              )}
            </article>
          </div>

          {/* Aside Right Column */}
          <div className="col-span-12 space-y-6 lg:col-span-4">
            {/* Operational Status Card */}
            <article className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-sm">
              <div className="mb-4 flex items-center gap-3">
                <div className="h-6 w-1 rounded-full bg-primary" />
                <h2 className="text-base font-bold text-on-surface">Operational Status</h2>
              </div>
              <p className="text-xs text-on-surface-variant leading-relaxed mb-4">
                Active schemes are available for immediate selection during student admission and fee voucher entry.
              </p>

              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => actions.setOperationalStatus('active')}
                  className={`flex flex-col items-center justify-center gap-2 rounded-xl border p-4 text-xs font-bold transition cursor-pointer ${
                    state.operationalStatus === 'active'
                      ? 'border-primary bg-primary/10 text-primary shadow-xs'
                      : 'border-outline-variant/30 bg-surface-container-low text-on-surface-variant hover:bg-surface-container-high'
                  }`}
                >
                  <span className="material-symbols-outlined text-2xl">check_circle</span>
                  <span>Active</span>
                </button>

                <button
                  type="button"
                  onClick={() => actions.setOperationalStatus('inactive')}
                  className={`flex flex-col items-center justify-center gap-2 rounded-xl border p-4 text-xs font-bold transition cursor-pointer ${
                    state.operationalStatus === 'inactive'
                      ? 'border-outline bg-surface-container-high text-on-surface shadow-xs'
                      : 'border-outline-variant/30 bg-surface-container-low text-on-surface-variant hover:bg-surface-container-high'
                  }`}
                >
                  <span className="material-symbols-outlined text-2xl">archive</span>
                  <span>Archived</span>
                </button>
              </div>
            </article>

            {/* Configuration Completion Card */}
            <article className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-sm">
              <div className="mb-4 flex items-center gap-3">
                <div className="h-6 w-1 rounded-full bg-primary" />
                <h2 className="text-base font-bold text-on-surface">Configuration Completion</h2>
              </div>

              <div className="flex items-center gap-4 mb-4">
                <div className="relative flex h-14 w-14 shrink-0 items-center justify-center">
                  <svg className="h-14 w-14 -rotate-90" viewBox="0 0 36 36">
                    <path
                      className="text-surface-container-high"
                      strokeWidth="3.5"
                      stroke="currentColor"
                      fill="none"
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                    <path
                      className="text-primary transition-all duration-300"
                      strokeDasharray={`${state.completionPercent}, 100`}
                      strokeWidth="3.5"
                      strokeLinecap="round"
                      stroke="currentColor"
                      fill="none"
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                  </svg>
                  <span className="absolute text-xs font-bold text-on-surface">
                    {state.completionPercent}%
                  </span>
                </div>
                <div>
                  <p className="text-xs font-bold text-on-surface">Scheme Readiness</p>
                  <p className="text-[11px] text-on-surface-variant">
                    {state.completionPercent === 100
                      ? 'Scheme is fully configured and ready to save.'
                      : 'Ensure all required fields and course rows are filled.'}
                  </p>
                </div>
              </div>

              <div className="space-y-2 border-t border-outline-variant/15 pt-3 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-on-surface-variant">Configured Courses:</span>
                  <strong className="text-on-surface font-semibold">{state.form.combo_grid.length}</strong>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-on-surface-variant">Subjects Assigned:</span>
                  <strong className="text-on-surface font-semibold">
                    {state.form.has_subjects ? state.form.subjects.length : 'None'}
                  </strong>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-on-surface-variant">Installments:</span>
                  <strong className="text-on-surface font-semibold">
                    {state.form.has_installment ? `${state.form.installment_grid.length} parts` : 'None'}
                  </strong>
                </div>
              </div>
            </article>
          </div>
        </section>
      )}
    </div>
  );
}
